import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useShippingAddress } from 'providers/ShippingAddressProvider';
import {
  ProposedShippingAddressDto,
  UserShippingAddressDto,
  UserShippingAddressRequestDto,
} from 'services/user/userService.dto';

type DisplayMode = 'NEW' | 'LIST' | 'SUGGESTION';

interface ShippingAddressUiContextType {
  shippingAddresses: UserShippingAddressDto[];
  defaultShippingAddress?: UserShippingAddressDto;
  proposedAddress?: ProposedShippingAddressDto;
  submittedAddressRequest?: UserShippingAddressRequestDto;
  initState: () => void;
  isLoading: boolean;
  isSaving: boolean;
  addShippingAddress: (request: UserShippingAddressRequestDto) => Promise<void>;
  updateShippingAddress: (id: number, request: UserShippingAddressRequestDto) => Promise<void>;
  deleteShippingAddress: (id: number) => Promise<void>;
  setDefaultShippingAddress: (id: number) => Promise<void>;
  editedShippingAddressId?: number;
  setEditedShippingAddressId: (id?: number) => void;
  setIsInsertMode: (value: boolean) => void;
  displayMode: DisplayMode;
}

const ShippingAddressUiContext = createContext<ShippingAddressUiContextType>(null!);

const ShippingAddressUiProvider = ({ children }: PropsWithChildren) => {
  const {
    shippingAddresses,
    defaultShippingAddress,
    isLoading,
    isLoaded,
    isSaving,
    addShippingAddress: saveAddress,
    updateShippingAddress: patchAddress,
    deleteShippingAddress: removeAddress,
    setDefaultShippingAddress: markDefault,
  } = useShippingAddress();

  const [editedShippingAddressId, setEditedShippingAddressId] = useState<number>();
  const [isInsertMode, setIsInsertMode] = useState(false);
  const [submittedAddressRequest, setSubmittedAddressRequest] = useState<UserShippingAddressRequestDto>();
  const [proposedAddress, setProposedAddress] = useState<ProposedShippingAddressDto>();
  const [didSyncInsertMode, setDidSyncInsertMode] = useState(false);

  useEffect(() => {
    if (!isLoaded || didSyncInsertMode) return;
    setIsInsertMode(shippingAddresses.length === 0);
    setDidSyncInsertMode(true);
  }, [isLoaded, shippingAddresses.length, didSyncInsertMode]);

  const displayMode: DisplayMode = useMemo(() => {
    if (isInsertMode) return 'NEW';
    if (!!proposedAddress && !!submittedAddressRequest) return 'SUGGESTION';
    return 'LIST';
  }, [isInsertMode, proposedAddress, submittedAddressRequest]);

  const initState = useCallback(() => {
    setEditedShippingAddressId(undefined);
    setSubmittedAddressRequest(undefined);
    setProposedAddress(undefined);
  }, []);

  const applySuccess = useCallback(
    (addresses: UserShippingAddressDto[]) => {
      setIsInsertMode(addresses.length === 0);
      initState();
    },
    [initState]
  );

  const applySaveResult = useCallback(
    (result: Awaited<ReturnType<typeof saveAddress>>) => {
      if (result.type === 'success') {
        applySuccess(result.addresses);
      } else if (result.type === 'proposed') {
        setIsInsertMode(false);
        setProposedAddress(result.proposedAddress);
        setSubmittedAddressRequest(result.request);
      }
    },
    [applySuccess]
  );

  const addShippingAddress = useCallback(
    async (request: UserShippingAddressRequestDto) => {
      applySaveResult(await saveAddress(request));
    },
    [applySaveResult, saveAddress]
  );

  const updateShippingAddress = useCallback(
    async (id: number, request: UserShippingAddressRequestDto) => {
      applySaveResult(await patchAddress(id, request));
    },
    [applySaveResult, patchAddress]
  );

  const deleteShippingAddress = useCallback(
    async (id: number) => {
      const result = await removeAddress(id);
      if (result.type === 'success') applySuccess(result.addresses);
    },
    [applySuccess, removeAddress]
  );

  const setDefaultShippingAddress = useCallback(
    async (id: number) => {
      const result = await markDefault(id);
      if (result.type === 'success') applySuccess(result.addresses);
    },
    [applySuccess, markDefault]
  );

  return (
    <ShippingAddressUiContext.Provider
      value={{
        shippingAddresses,
        defaultShippingAddress,
        proposedAddress,
        submittedAddressRequest,
        isLoading,
        isSaving,
        initState,
        addShippingAddress,
        updateShippingAddress,
        deleteShippingAddress,
        setDefaultShippingAddress,
        editedShippingAddressId,
        setEditedShippingAddressId,
        setIsInsertMode,
        displayMode,
      }}>
      {children}
    </ShippingAddressUiContext.Provider>
  );
};

const useShippingAddressUi = () => useContext(ShippingAddressUiContext);

export { ShippingAddressUiProvider, useShippingAddressUi };
